import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { refresh as refreshTokenApi } from '@/apis';
import { CommonRes } from '@/types';
import { RequestContext, requestManager } from '@/utils/RequestManager';
import { safeApiCall } from '@/utils/safeApiCall';

const API_URL = process.env['APP_URL']
  ? `${process.env['APP_URL']}/api`
  : '/api';

/**
 * Extended Axios config with requestKey support
 */
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  /** Request key for race condition prevention and cancellation */
  requestKey?: string;
}

type UseAxiosFunction = (() => HttpMethods) & {
  apiClient: AxiosInstance;
};

interface HttpMethods {
  get<T = unknown>(
    url: string,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<CommonRes<T>>;
  post<T = unknown>(
    url: string,
    data?: unknown,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<CommonRes<T>>;
  put<T = unknown>(
    url: string,
    data?: unknown,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<CommonRes<T>>;
  delete<T = unknown>(
    url: string,
    config?: ExtendedAxiosRequestConfig,
  ): Promise<CommonRes<T>>;
  instance: AxiosInstance;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

export function setupInterceptors(
  getAccessToken: () => string | null,
  setAccessToken: (token: string | null) => void,
  logout: () => Promise<void>,
  startLoading?: (options?: { global?: boolean }) => void,
  stopLoading?: (options?: { global?: boolean }) => void,
): () => void {
  const req = apiClient.interceptors.request.use((config) => {
    // 只有特定請求才顯示全局 loading
    const showGlobalLoading = config.headers?.['X-Show-Loading'] !== 'false';
    if (startLoading && showGlobalLoading) {
      startLoading({ global: true });
    }
    const token = getAccessToken();
    if (token) {
      if (!config.headers) {
        config.headers = new AxiosHeaders();
      }
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
  });

  let refreshPromise: Promise<string | null> | null = null;

  async function refreshToken(): Promise<string | null> {
    if (!refreshPromise) {
      refreshPromise = safeApiCall(() => refreshTokenApi())
        .then((data) => {
          if (data) {
            setAccessToken(data.token);
            return data.token;
          }
          const fakeResponse: AxiosResponse = {
            data: null,
            status: 401,
            statusText: 'Unauthorized',
            headers: {},
            config: { headers: new axios.AxiosHeaders() },
          };

          throw new AxiosError(
            'Unauthorized',
            undefined,
            undefined,
            {},
            fakeResponse,
          );
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
    return refreshPromise;
  }

  const res = apiClient.interceptors.response.use(
    (resp: AxiosResponse) => {
      const showGlobalLoading =
        resp.config.headers?.['X-Show-Loading'] !== 'false';
      if (stopLoading && showGlobalLoading) {
        stopLoading({ global: true });
      }
      return resp;
    },
    async (error: unknown) => {
      if (stopLoading && isAxiosError(error)) {
        const showGlobalLoading =
          error.config?.headers?.['X-Show-Loading'] !== 'false';
        if (showGlobalLoading) {
          stopLoading({ global: true });
        }
      }
      if (!isAxiosError(error)) {
        return Promise.reject(error);
      }
      const original = error.config;
      if (!original) {
        return Promise.reject(error);
      }
      if (
        error.response?.status === 401 &&
        !original.__isRetryRequest &&
        !original.url?.includes('/auth/refresh') &&
        !original.url?.includes('/auth/login')
      ) {
        try {
          const newToken = await refreshToken();
          if (newToken) {
            if (!original.headers) {
              original.headers = new AxiosHeaders();
            }
            original.headers.set('Authorization', `Bearer ${newToken}`);
            original.__isRetryRequest = true;
            return apiClient(original);
          }
        } catch (refreshError) {
          if (
            isAxiosError(refreshError) &&
            refreshError.response?.status === 401
          ) {
            await logout();
            setAccessToken(null);
          }
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error);
    },
  );

  return () => {
    apiClient.interceptors.request.eject(req);
    apiClient.interceptors.response.eject(res);
  };
}

/**
 * Prepare request with AbortController support
 * Handles requestKey from config or RequestContext
 */
function prepareRequest(requestKey?: string) {
  // Get requestKey from config or RequestContext
  const effectiveRequestKey = requestKey ?? RequestContext.getKey();

  if (effectiveRequestKey) {
    // If pending request exists with same key, cancel it (prevent race condition)
    const existing = requestManager.getRequestInfo(effectiveRequestKey);
    if (existing && existing.status === 'pending') {
      requestManager.cancel(effectiveRequestKey, 'Replaced by new request');
    }

    // Create new AbortController and register to RequestManager
    const controller = new AbortController();
    requestManager.register(effectiveRequestKey, controller);

    return {
      signal: controller.signal,
      cleanup: () => {
        requestManager.cleanup(effectiveRequestKey);
      },
    };
  }

  // No requestKey, create temporary controller (not registered to manager)
  const controller = new AbortController();
  return {
    signal: controller.signal,
    cleanup: () => {}, // No cleanup needed
  };
}

const useAxios: UseAxiosFunction = () =>
  ({
    get: async <T>(url: string, config?: ExtendedAxiosRequestConfig) => {
      const { requestKey, ...axiosConfig } = config || {};
      const { signal, cleanup } = prepareRequest(requestKey);

      try {
        const res = await apiClient.get<CommonRes<T>>(url, {
          ...axiosConfig,
          signal: signal || axiosConfig.signal,
        });
        return res.data;
      } finally {
        cleanup();
      }
    },
    post: async <T>(
      url: string,
      data?: unknown,
      config?: ExtendedAxiosRequestConfig,
    ) => {
      const { requestKey, ...axiosConfig } = config || {};
      const { signal, cleanup } = prepareRequest(requestKey);

      try {
        const res = await apiClient.post<CommonRes<T>>(url, data, {
          ...axiosConfig,
          signal: signal || axiosConfig.signal,
        });
        return res.data;
      } finally {
        cleanup();
      }
    },
    put: async <T>(
      url: string,
      data?: unknown,
      config?: ExtendedAxiosRequestConfig,
    ) => {
      const { requestKey, ...axiosConfig } = config || {};
      const { signal, cleanup } = prepareRequest(requestKey);

      try {
        const res = await apiClient.put<CommonRes<T>>(url, data, {
          ...axiosConfig,
          signal: signal || axiosConfig.signal,
        });
        return res.data;
      } finally {
        cleanup();
      }
    },
    delete: async <T>(url: string, config?: ExtendedAxiosRequestConfig) => {
      const { requestKey, ...axiosConfig } = config || {};
      const { signal, cleanup } = prepareRequest(requestKey);

      try {
        const res = await apiClient.delete<CommonRes<T>>(url, {
          ...axiosConfig,
          signal: signal || axiosConfig.signal,
        });
        return res.data;
      } finally {
        cleanup();
      }
    },
    instance: apiClient,
  }) as HttpMethods & { apiClient: AxiosInstance };

useAxios.apiClient = apiClient;

/**
 * Global wrapped API client with RequestContext support
 * Use this in API utility functions to support automatic request cancellation
 */
export const wrappedApiClient = {
  get: async <T>(url: string, config?: ExtendedAxiosRequestConfig) => {
    const { requestKey, ...axiosConfig} = config || {};
    const { signal, cleanup } = prepareRequest(requestKey);

    try {
      const res = await apiClient.get<CommonRes<T>>(url, {
        ...axiosConfig,
        signal: signal || axiosConfig.signal,
      });
      return res.data;
    } finally {
      cleanup();
    }
  },
  /**
   * GET request that returns raw response data (not wrapped in CommonRes)
   * Useful for blob responses, file downloads, etc.
   */
  getRaw: async <T = unknown>(url: string, config?: ExtendedAxiosRequestConfig) => {
    const { requestKey, ...axiosConfig } = config || {};
    const { signal, cleanup } = prepareRequest(requestKey);

    try {
      const res = await apiClient.get<T>(url, {
        ...axiosConfig,
        signal: signal || axiosConfig.signal,
      });
      return res.data;
    } finally {
      cleanup();
    }
  },
  post: async <T>(
    url: string,
    data?: unknown,
    config?: ExtendedAxiosRequestConfig,
  ) => {
    const { requestKey, ...axiosConfig } = config || {};
    const { signal, cleanup } = prepareRequest(requestKey);

    try {
      const res = await apiClient.post<CommonRes<T>>(url, data, {
        ...axiosConfig,
        signal: signal || axiosConfig.signal,
      });
      return res.data;
    } finally {
      cleanup();
    }
  },
  put: async <T>(
    url: string,
    data?: unknown,
    config?: ExtendedAxiosRequestConfig,
  ) => {
    const { requestKey, ...axiosConfig } = config || {};
    const { signal, cleanup } = prepareRequest(requestKey);

    try {
      const res = await apiClient.put<CommonRes<T>>(url, data, {
        ...axiosConfig,
        signal: signal || axiosConfig.signal,
      });
      return res.data;
    } finally {
      cleanup();
    }
  },
  delete: async <T>(url: string, config?: ExtendedAxiosRequestConfig) => {
    const { requestKey, ...axiosConfig } = config || {};
    const { signal, cleanup } = prepareRequest(requestKey);

    try {
      const res = await apiClient.delete<CommonRes<T>>(url, {
        ...axiosConfig,
        signal: signal || axiosConfig.signal,
      });
      return res.data;
    } finally {
      cleanup();
    }
  },
};

export default useAxios;
